import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';

import * as styles from './AnimatedRow.module.scss';

const AnimatedRow = ({ heading, logos = [] }) => {
  const [activeList, setActiveList] = useState(0);
  const [usersTimezone, setUsersTimezone] = useState(null);

  useEffect(() => {
    setUsersTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
  }, []);

  const localizedLogos = useMemo(() => {
    if (!usersTimezone) return null;

    /*
    Locale lists came from manually cross-referencing
    timeZone lists and various country group lists (EMEA, etc.).
    If we do many more geo-personalized features like this,
    we should consider using a 3rd-party library or something.
  */
    const dach = ['Busingen', 'Berlin', 'Vienna', 'Zurich'];
    const emea = [
      'Africa',
      'Europe',
      'Reykjavik',
      'Yekaterinburg',
      'Omsk',
      'Novosibirsk',
      'Barnaul',
      'Tomsk',
      'Novokuznetsk',
      'Krasnoyarsk',
      'Irkutsk',
      'Chita',
      'Yakutsk',
      'Khandyga',
      'Vladivostok',
      'Ust-Nera',
      'Magadan',
      'Sakhalin',
      'Srednekolymsk',
      'Kamchatka',
      'Anadyr',
      'Tehran',
      'Dubai',
      'Baghdad',
      'Riyadh',
      'Aden',
      'Damascus',
      'Port_Moresby',
      'Bougainville',
      'Amman',
      'Madeira',
      'Azores',
      'Jerusalem',
      'Beirut',
      'Gaza',
      'Hebron',
      'Muscat',
      'Kuwait',
      'Tbilisi',
      'Qatar',
      'Bahrain',
      'Mauritius',
      'Nicosia',
      'Famagusta',
      'Reunion',
      'Mayotte',
      'Mahe',
      'Faroe',
    ];
    const latam = [
      'Belize',
      'Costa_Rica',
      'Havana',
      'Santo_Domingo',
      'El_Salvador',
      'Guatemala',
      'Port-au-Prince',
      'Tegucigalpa',
      'Jamaica',
      'Mexico_City',
      'Cancun',
      'Merida',
      'Monterrey',
      'Matamoros',
      'Mazatlan',
      'Chihuahua',
      'Ojinaga',
      'Hermosillo',
      'Tijuana',
      'Bahia_Banderas',
      'Managua',
      'St_Lucia',
      'Buenos_Aires',
      'Cordoba',
      'Salta',
      'Jujuy',
      'Tucuman',
      'Catamarca',
      'La_Rioja',
      'San_Juan',
      'Mendoza',
      'San_Luis',
      'Rio_Gallegos',
      'Ushuaia',
      'La_Paz',
      'Noronha',
      'Belem',
      'Fortaleza',
      'Recife',
      'Araguaina',
      'Maceio',
      'Bahia',
      'Sao_Paulo',
      'Campo_Grande',
      'Cuiaba',
      'Santarem',
      'Porto_Velho',
      'Boa_Vista',
      'Manaus',
      'Eirunepe',
      'Rio_Branco',
      'Santiago',
      'Punta_Arenas',
      'Easter',
      'Bogota',
      'Guayaquil',
      'Galapagos',
      'Asuncion',
      'Lima',
      'Montevideo',
      'Caracas',
      'Canary',
      'Antananarivo',
      'Comoro',
      'Cape_Verde',
    ];
    const apac = ['Australia', 'Macquarie', 'Auckland', 'Chatham', 'Asia'];

    const checkLocale = (list) => list.some((locale) => usersTimezone.includes(locale));
    const getLogos = (locale) => logos.filter((group) => group.title?.toLowerCase().includes(locale));

    /* 
    This retrieves personalized logos based on user location.
    We must check DACH first because EMEA would also return `true`
    for people in the DACH region.
  */
    let brandsInGeo;
    const defaultLogos = getLogos('default');

    if (checkLocale(dach)) {
      brandsInGeo = getLogos('dach');
    } else if (checkLocale(emea)) {
      brandsInGeo = getLogos('emea');
    } else if (checkLocale(latam)) {
      brandsInGeo = getLogos('latam');
    } else if (checkLocale(apac)) {
      brandsInGeo = getLogos('apac');
    } else if (defaultLogos.length > 0) {
      brandsInGeo = defaultLogos;
    } else {
      brandsInGeo = [{ media: logos.map((l) => l.media).flat() }];
    }

    // Divide logos into sets of 6
    const logoSubsets = [];
    for (let i = 0; i < brandsInGeo[0].media.length; i += 6) {
      logoSubsets.push(brandsInGeo[0].media.slice(i, i + 6));
    }
    return logoSubsets;
  }, [logos, usersTimezone]);

  useEffect(() => {
    const animationInterval = setInterval(
      () => (activeList === localizedLogos.length - 1 ? setActiveList(0) : setActiveList(activeList + 1)),
      3000
    );

    return () => clearInterval(animationInterval);
  }, [localizedLogos, activeList]);

  const animatedLogos = localizedLogos?.map((logoSet, i) => {
    return (
      <ul
        data-analytics-component={'logo-list'}
        key={i}
        className={[styles.logoSet, activeList === i ? styles.visible : null].join(' ')}
      >
        {logoSet.map((logo, j) => (
          <li
            data-analytics-component={'logo'}
            data-analytics-id={logo.title}
            key={j}
            style={{ '--delay': `${100 * j}ms` }}
          >
            {logo.svg?.content ? (
              <div dangerouslySetInnerHTML={{ __html: logo.svg.content }} />
            ) : (
              <img src={logo.file.url} loading="lazy" alt={logo.title} />
            )}
          </li>
        ))}
      </ul>
    );
  });

  return (
    <div className={styles.section} data-analytics-section={'animation-row-logos'}>
      <h2 className={styles.heading}>{heading}</h2>
      <div className={styles.logos}>{animatedLogos}</div>
    </div>
  );
};

AnimatedRow.propTypes = {
  heading: PropTypes.string,
  logos: PropTypes.array,
};

export default AnimatedRow;
